import { LogOptions, LoggerAdapter, LogEntry } from '../types';

/**
 * Implements a chainable logger that allows applying options before
 * executing the log. The log is committed at the end of the microtask queue.
 */
export class LoggerChain {
  private committed = false;
  private chainOptions: LogOptions = {};
  private method: keyof LoggerAdapter | null = null;
  private stack = '';
  private args: any[] = [];

  constructor(private adapter: LoggerAdapter, initialOptions?: LogOptions) {
    if (initialOptions) {
      this.chainOptions = { ...initialOptions };
    }
    // Schedule the commit at the end of the current microtask
    Promise.resolve().then(() => this.commit());
  }

  public options(opts: LogOptions): this {
    this.chainOptions = { ...this.chainOptions, ...opts };
    return this;
  }

  public trace(...args: any[]): this {
    return this.log('trace', args);
  }

  public debug(...args: any[]): this {
    return this.log('debug', args);
  }

  public info(...args: any[]): this {
    return this.log('info', args);
  }

  public warn(...args: any[]): this {
    return this.log('warn', args);
  }

  public error(...args: any[]): this {
    return this.log('error', args);
  }

  public fatal(...args: any[]): this {
    return this.log('fatal', args);
  }

  private log(method: keyof LoggerAdapter, args: any[]): this {
    this.method = method;
    this.args = args;
    this.stack = new Error().stack ?? '';
    return this;
  }

  private commit(): void {
    if (this.committed) return;
    this.committed = true;

    if (this.method) {
      const message = this.args[0];
      const args = this.args.slice(1);
      const entry: LogEntry = {
        stack: this.stack,
        content: { message, args },
        options: this.chainOptions,
      };

      (this.adapter as any)[this.method](entry);
    }
  }
}
